import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EmbeddedBlock, EmbeddedBlockVariant, CssSelectorMap } from '@tinct/core-api-models';
import { withTwins } from '@tinct/core-dom';
import { getCandidatesForWidth } from './css-selector-utils';
import { renderBlockVariant } from './block-variant-renderers';

export type CssSelectorMatch = { elements: Element[]; screenSize: string; selector: string };

export type FindElementsOptions = {
  /**
   * Also return the twins of each match — copies of the same text the
   * selector misses, e.g. stripe.com's hero stacks a second `<h1>` over the
   * first for a blend effect and the positional selector reaches one of them
   * (`@tinct/core-dom`'s `withTwins`). For text variants; logo renderers own
   * their targets.
   */
  includeTwins?: boolean;
};

/**
 * Every element the variant's selectors match — the first selector (in the
 * width's candidate order) that matches anything wins, and ALL of its matches
 * are returned. Same contract as the SDK's `applyBlocks`: pages routinely
 * duplicate an element under one selector (text layered for a visual
 * effect, desktop/mobile copies of a header, carousel clones), and the live
 * page personalizes every copy — so must the preview, or the copies fall out
 * of sync and both show through.
 */
export function findElementsByCssSelectors(
  cssSelectors: CssSelectorMap,
  doc: Document,
  { includeTwins = false }: FindElementsOptions = {}
): CssSelectorMatch | null {
  const width = doc.defaultView?.innerWidth ?? 1280;
  const candidates = getCandidatesForWidth(cssSelectors, width);

  for (const { screenSize, selectors } of candidates) {
    for (const selector of selectors) {
      try {
        const matches = Array.from(doc.querySelectorAll(selector)).filter(
          (element) => !element.closest('[data-tinct-overlay]')
        );
        if (matches.length > 0) {
          const elements = includeTwins ? withTwins(matches) : matches;
          return { elements, screenSize, selector };
        }
      } catch {
        // Skip invalid selectors
      }
    }
  }

  return null;
}

// Exported for the inline text editor, which edits ONE copy (the first) and
// lets the re-apply after save propagate the value to the others.
export function findElementByCssSelectors(
  cssSelectors: CssSelectorMap,
  doc: Document
): { element: Element; screenSize: string; selector: string } | null {
  const match = findElementsByCssSelectors(cssSelectors, doc);
  return match ? { element: match.elements[0], screenSize: match.screenSize, selector: match.selector } : null;
}

export type UseBlockVariantReplacerOptions = {
  contentDocument: Document | null;
  blockVariants: EmbeddedBlockVariant[];
  /** Blocks the variants belong to; forwarded to each renderer. */
  blocks: EmbeddedBlock[];
};

type ReplacementRecord = {
  element: Element;
  originalContent: string;
  /**
   * The element's inline `style` attribute before the renderer's
   * `elementStyle` was applied (`null` = no attribute). `undefined` means the
   * style was never touched and must not be restored.
   */
  originalStyle?: string | null;
  /**
   * An in-place rendering (see `InPlaceRendering`) is applied over the live
   * nodes and re-applied over them too: its element is kept as is across
   * re-apply passes and only restored on teardown (see `restore`).
   */
  inPlace?: boolean;
};

/** Puts a targeted element back to its snapshot (content, and style when overridden). */
function restoreRecord({ element, originalContent, originalStyle }: ReplacementRecord): void {
  restoreContentPreservingOverlays(element, originalContent);
  if (originalStyle !== undefined) {
    if (originalStyle === null) element.removeAttribute('style');
    else element.setAttribute('style', originalStyle);
  }
}

/** A located target rendered via a React portal (e.g. the logos banner). */
type ReactTarget = {
  id: string;
  element: Element;
  node: ReactNode;
};

/**
 * Replaces innerHTML while preserving any overlay nodes (React portals)
 * that are children of the element. Exported (with the restore helper below)
 * for the inline text editor, which snapshots/restores the same way.
 */
export function collectOverlayNodes(element: Element): Element[] {
  return Array.from(element.querySelectorAll(':scope > [data-tinct-overlay]'));
}

export function replaceContentPreservingOverlays(element: Element, newHtml: string): void {
  // Detach overlay nodes before replacing content
  const overlays = collectOverlayNodes(element);
  overlays.forEach((overlay) => overlay.remove());

  element.innerHTML = newHtml.replace(/\n/g, '');

  // Re-append overlay nodes so React portals stay intact
  overlays.forEach((overlay) => element.appendChild(overlay));
}

/**
 * Restores original innerHTML while preserving overlay nodes.
 */
export function restoreContentPreservingOverlays(element: Element, originalHtml: string): void {
  const overlays = collectOverlayNodes(element);
  overlays.forEach((overlay) => overlay.remove());

  element.innerHTML = originalHtml;

  overlays.forEach((overlay) => element.appendChild(overlay));
}

/**
 * Hook that replaces the content of elements targeted by block variants.
 *
 * The per-type behaviour lives in `block-variant-renderers/` (one file per
 * block type + a default): each renderer turns a variant into either an HTML
 * string (injected as innerHTML) or a React node (cleared + portaled into the
 * target). This hook only handles the shared DOM machinery — locating elements,
 * preserving/restoring content, and keeping portals in sync.
 *
 * Preserves [data-tinct-overlay] children (React portal nodes — including any
 * rendered block-variant node) across replacements, and restores original
 * content on cleanup or when variants change.
 *
 * A MutationObserver watches the targeted elements and re-applies variants
 * whenever external code mutates the DOM (e.g. the source HTML being
 * re-injected). The replacer's own writes are ignored via an `isApplying` flag +
 * `takeRecords()`, and mutations inside `[data-tinct-overlay]` subtrees (React
 * portals) are filtered out.
 *
 * A re-apply pass restores html/react targets to their snapshot first, but
 * leaves in-place targets as they are and applies over them again: their
 * renderer mutates the page's own nodes and is built to be re-run on them
 * (the SDK's logo engine persists each visual's identity and the row's
 * Swapping a live slider's content for a snapshot would orphan the
 * slider's node references (its marquee then drives detached nodes, or
 * jumps between positions after a re-measure). The same holds across effect
 * rebuilds — a variants refetch changes the signature at any time, including
 * after the slider mounted — so a rebuild hands its in-place records to the
 * next instance and only the hook's unmount restores them (or a pass that no
 * longer matches them).
 *
 * The document is also re-applied as it finishes loading: `useShadowDom`
 * publishes it as soon as its `<body>` exists, which for a large capture is
 * while the HTML is still streaming in — elements further down the page are
 * not parsed yet, their variants match nothing, and nothing would ever retry
 * (the document identity never changes, and the observer only watches what
 * was matched). Which artboard came out personalized used to depend on how
 * much of the page had parsed by the time the document was published.
 */
export function useBlockVariantReplacer({
  contentDocument,
  blockVariants,
  blocks,
}: UseBlockVariantReplacerOptions): ReactNode {
  const replacementsRef = useRef<ReplacementRecord[]>([]);
  const [reactTargets, setReactTargets] = useState<ReactTarget[]>([]);
  const reactTargetsSignatureRef = useRef('');

  // Stable content signature of the variants. Parents pass a new `blockVariants`
  // array identity on every render (`.filter(...)`, `?? []`), so depending on the
  // array itself would re-run the effect each render — and its cleanup's
  // `setReactTargets([])` (a fresh array) would re-render, looping forever.
  // Keying on this string instead rebuilds the effect only when the variants'
  // meaningful content actually changes.
  // Memoized on the array identity: when a parent does hand us a stable array
  // (the studio canvas does, across a whole pan/zoom gesture) we skip
  // serializing every variant's full HTML on each render.
  const blockVariantsSignature = useMemo(
    () =>
      JSON.stringify(
        blockVariants.map((variant) => ({
          id: variant.id,
          type: variant.type,
          value: variant.value,
          cssSelectors: variant.cssSelectors,
          // Id and url: a company with only a brandmark has no `logo` url, so two
          // such companies swapped by a re-match would otherwise look identical.
          logos: variant.companies?.map((company) => [company.id, company.logo?.url]),
        }))
      ),
    [blockVariants]
  );

  // The effect keys on the signature (above), so it reads the variants from a
  // ref to always see the latest content without taking the unstable array as a
  // dependency. The ref is synced in its own effect (writing it during render is
  // disallowed).
  const blockVariantsRef = useRef(blockVariants);
  useEffect(() => {
    blockVariantsRef.current = blockVariants;
  }, [blockVariants]);

  // Same ref pattern for `blocks`, so apply() can resolve each variant's block
  // without taking the unstable array as an effect dependency.
  const blocksRef = useRef(blocks);
  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    if (!contentDocument) return;
    const body = contentDocument.body;
    if (!body) return;

    let isApplying = false;
    let disposed = false;

    // Handed to in-place renderers for async refinements (e.g. logo natural
    // dimensions resolving): schedules a fresh full pass, unless the effect
    // is gone. Deferred so it can never re-enter a running apply().
    const requestReapply = () => {
      if (disposed) return;
      setTimeout(() => {
        if (!disposed) apply();
      }, 0);
    };

    /**
     * Restores every targeted element to its snapshot. Before a re-apply
     * pass or a rebuild (`keepInPlace`), in-place targets still in the
     * document are kept as they are — the next pass applies over them again
     * — and returned so the pass can restore the ones it no longer matches.
     */
    const restore = ({ keepInPlace = false } = {}): Map<Element, ReplacementRecord> => {
      // An element under inline text edition (`data-tinct-editing`, see
      // useInlineTextEditor) holds the user's in-progress typing — leave its
      // DOM alone and retain its record so the pre-variant snapshot survives
      // a mid-edit rebuild triggered by another variant changing.
      const retained: ReplacementRecord[] = [];
      const keptInPlace = new Map<Element, ReplacementRecord>();
      replacementsRef.current.forEach((record) => {
        const { element } = record;
        if (element.closest('[data-tinct-editing]')) {
          retained.push(record);
          return;
        }
        if (keepInPlace && record.inPlace && element.isConnected) {
          retained.push(record);
          keptInPlace.set(element, record);
          return;
        }
        restoreRecord(record);
      });
      replacementsRef.current = retained;
      return keptInPlace;
    };

    const apply = () => {
      if (isApplying) return;
      isApplying = true;
      observer.disconnect();
      try {
        const keptInPlace = restore({ keepInPlace: true });

        const nextReactTargets: ReactTarget[] = [];
        const signatureParts: string[] = [];

        blockVariantsRef.current.forEach((variant) => {
          if (!variant.cssSelectors) return;

          // Prefer the live block from `blocks`; fall back to the variant's
          // embedded block. Skip if neither exists.
          const block =
            blocksRef.current.find((b) => b.id === variant.block?.id) ?? variant.block;
          if (!block) return;

          const rendering = renderBlockVariant(variant, block);
          if (!rendering) return;

          // restore() ran first, so each match holds its original text and
          // its twins (same original text) resolve; they hold the personalized
          // text together afterwards, so the same set resolves on re-apply.
          const match = findElementsByCssSelectors(variant.cssSelectors, contentDocument, {
            includeTwins: rendering.kind === 'html',
          });
          if (!match) return;

          // Every copy the selector matches gets the variant (see
          // findElementsByCssSelectors) — each with its own restore record.
          match.elements.forEach((element, index) => {
            // Under inline text edition — the editor owns this element's DOM
            // until the edit settles; its retained record (see restore) already
            // holds the pre-variant snapshot.
            if (element.closest('[data-tinct-editing]')) return;

            // Kept from the previous pass (see restore): its snapshot record
            // still stands, and the renderer applies over the live nodes. A
            // rendering that is no longer in-place (the variant opted out of
            // templating) restores the snapshot first, so the fresh record
            // below captures the page's original content, not ours.
            const kept = keptInPlace.get(element);
            if (kept) {
              keptInPlace.delete(element);
              if (rendering.kind === 'in-place') {
                rendering.apply(element, requestReapply, index);
                return;
              }
              restoreRecord(kept);
              replacementsRef.current = replacementsRef.current.filter((r) => r !== kept);
            }

            // Store original content for restoration (exclude overlay nodes)
            const overlays = collectOverlayNodes(element);
            overlays.forEach((overlay) => overlay.remove());
            const originalContent = element.innerHTML;
            overlays.forEach((overlay) => element.appendChild(overlay));

            replacementsRef.current.push({
              element,
              originalContent,
              // Only remembered (and later restored) when we override the style.
              originalStyle: rendering.elementStyle
                ? element.getAttribute('style')
                : undefined,
              inPlace: rendering.kind === 'in-place',
            });

            // Renderer-specified inline CSS for the matched element (camelCase
            // CSSProperties keys map directly onto CSSStyleDeclaration).
            if (rendering.elementStyle) {
              Object.assign((element as HTMLElement).style, rendering.elementStyle);
            }

            if (rendering.kind === 'react') {
              // Clear the original content; the portaled node (a
              // data-tinct-overlay child) is preserved and shows in its place.
              replaceContentPreservingOverlays(element, '');
              // One portal per copy: the id must stay unique across copies.
              const id = `${variant.id ?? match.selector}:${index}`;
              nextReactTargets.push({ id, element, node: rendering.node });
              signatureParts.push(`${id}:${rendering.signature}`);
            } else if (rendering.kind === 'in-place') {
              // The renderer mutates the element's existing content directly
              // (the snapshot above still restores it); its writes happen while
              // the observer is disconnected, like any other rendering's.
              rendering.apply(element, requestReapply, index);
            } else {
              // Replace with the rendered HTML, keeping overlays
              replaceContentPreservingOverlays(element, rendering.html);
            }
          });
        });

        // In-place targets this pass no longer matched: back to their snapshot.
        keptInPlace.forEach((record) => {
          restoreRecord(record);
          replacementsRef.current = replacementsRef.current.filter((r) => r !== record);
        });

        // Only push new state when the portal targets actually changed, so
        // external mutations that don't touch them don't trigger re-renders.
        const signature = signatureParts.join(';');
        if (signature !== reactTargetsSignatureRef.current) {
          reactTargetsSignatureRef.current = signature;
          setReactTargets(nextReactTargets);
        }

        // Only observe the targeted elements, not the whole document.
        replacementsRef.current.forEach(({ element }) => {
          observer.observe(element, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        });
      } finally {
        // Drop the mutation records our own writes just produced so the observer
        // doesn't re-trigger apply() in a feedback loop.
        observer.takeRecords();
        isApplying = false;
      }
    };

    const observer = new MutationObserver((records) => {
      if (isApplying) return;
      // Ignore mutations that happen inside overlay portals (React updates),
      // AND childList mutations where every added/removed node is an overlay
      // (e.g. BlockOverlay or a rendered variant node mounting as a direct
      // child of the target).
      const hasExternalChange = records.some((r) => {
        const el =
          r.target.nodeType === Node.ELEMENT_NODE
            ? (r.target as Element)
            : r.target.parentElement;
        if (!el || el.closest('[data-tinct-overlay]')) return false;
        // Typing inside an inline-edited element is not an external change —
        // re-applying here would wipe the user's edit on every keystroke.
        if (el.closest('[data-tinct-editing]')) return false;
        const touchedNodes = [...r.addedNodes, ...r.removedNodes];
        if (touchedNodes.length > 0) {
          const allOverlays = touchedNodes.every(
            (n) =>
              n instanceof Element &&
              (n.hasAttribute('data-tinct-overlay') ||
                !!n.closest('[data-tinct-overlay]'))
          );
          if (allOverlays) return false;
        }
        return true;
      });
      if (hasExternalChange) apply();
    });

    apply();

    // Parsing ends (`interactive`: every element exists) and the page's own
    // scripts have run (`complete`: a slider has cloned its slides, a builder
    // runtime has laid out its sections). Deferred through requestReapply so
    // it runs after the other listeners of the same transition.
    const onReadyStateChange = () => requestReapply();
    if (contentDocument.readyState !== 'complete') {
      contentDocument.addEventListener('readystatechange', onReadyStateChange);
    }

    return () => {
      disposed = true;
      contentDocument.removeEventListener('readystatechange', onReadyStateChange);
      observer.disconnect();
      // In-place targets stay for the next instance's first pass (which
      // adopts or restores them); the unmount effect below restores the rest.
      restore({ keepInPlace: true });
      reactTargetsSignatureRef.current = '';
      // Avoid a fresh-array re-render when there are no portal targets.
      setReactTargets((prev) => (prev.length === 0 ? prev : []));
    };
    // Variants are read from `blockVariantsRef`; we key on their content
    // signature so the effect only rebuilds when they meaningfully change.
  }, [contentDocument, blockVariantsSignature]);

  // Unmount only: whatever a rebuild handed over and no instance adopted.
  useEffect(
    () => () => {
      replacementsRef.current.forEach(restoreRecord);
      replacementsRef.current = [];
    },
    []
  );

  return reactTargets.map(({ id, element, node }) =>
    createPortal(
      <div data-tinct-overlay="" style={{ width: '100%' }}>
        {node}
      </div>,
      element,
      id
    )
  );
}
